// Superficie do editor: a previa REAL (renderizada pelo bot) ao fundo e as
// caixas das camadas por cima, arrastaveis e redimensionaveis. Posicoes em
// fracoes da tela (0-1), exatamente o que vai para o banco.

import { useRef, type KeyboardEvent, type PointerEvent, type RefObject } from "react";
import { Loader2, ImageOff } from "lucide-react";
import type { NormBox, WatermarkDesign, WatermarkLayer } from "@lucralink/shared";
import { LAYER_LABELS, moveBox, resizeBox, updateLayer } from "@/lib/watermark";
import type { PreviewState } from "@/lib/hooks/useWatermark";
import { cn } from "@/lib/utils";

interface WatermarkCanvasProps {
  readonly design: WatermarkDesign;
  readonly preview: PreviewState;
  readonly selectedId: string | null;
  readonly onSelect: (id: string | null) => void;
  readonly onChange: (design: WatermarkDesign) => void;
}

const KEY_STEP = 0.01;
const ARROWS: Readonly<Record<string, [number, number]>> = {
  ArrowLeft: [-KEY_STEP, 0],
  ArrowRight: [KEY_STEP, 0],
  ArrowUp: [0, -KEY_STEP],
  ArrowDown: [0, KEY_STEP],
};

type DragMode = "move" | "resize";

function pct(n: number): string {
  return `${(n * 100).toFixed(3)}%`;
}

type BoxChange = (id: string, box: NormBox) => void;

/** Arraste com ponteiro: deltas em fracao do tamanho real da superficie. */
function startDrag(
  event: PointerEvent<HTMLElement>,
  surface: RefObject<HTMLDivElement>,
  layer: WatermarkLayer,
  mode: DragMode,
  onBox: BoxChange,
): void {
  event.preventDefault();
  event.stopPropagation();
  const rect = surface.current?.getBoundingClientRect();
  if (!rect || rect.width === 0) return;
  const startX = event.clientX;
  const startY = event.clientY;
  event.currentTarget.setPointerCapture?.(event.pointerId);
  const move = (e: globalThis.PointerEvent) => {
    const dx = (e.clientX - startX) / rect.width;
    const dy = (e.clientY - startY) / rect.height;
    onBox(layer.id, mode === "move" ? moveBox(layer.box, dx, dy) : resizeBox(layer.box, dx, dy));
  };
  const stop = () => {
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", stop);
  };
  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", stop);
}

/** Setas movem 1%; Shift+setas redimensionam. */
function onArrow(event: KeyboardEvent<HTMLElement>, layer: WatermarkLayer, onBox: BoxChange): void {
  const delta = ARROWS[event.key];
  if (!delta) return;
  event.preventDefault();
  const [dx, dy] = delta;
  onBox(layer.id, event.shiftKey ? resizeBox(layer.box, dx, dy) : moveBox(layer.box, dx, dy));
}

interface LayerBoxProps {
  readonly layer: WatermarkLayer;
  readonly selected: boolean;
  readonly surface: RefObject<HTMLDivElement>;
  readonly onSelect: (id: string) => void;
  readonly onBox: BoxChange;
}

function LayerBox({ layer, selected, surface, onSelect, onBox }: LayerBoxProps): JSX.Element {
  const drag = (event: PointerEvent<HTMLElement>, mode: DragMode) => {
    onSelect(layer.id);
    startDrag(event, surface, layer, mode, onBox);
  };
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`${LAYER_LABELS[layer.kind]} (${layer.id}). Arraste para mover; setas movem, Shift+setas redimensionam.`}
      aria-pressed={selected}
      onPointerDown={(e) => drag(e, "move")}
      onKeyDown={(e) => onArrow(e, layer, onBox)}
      onFocus={() => onSelect(layer.id)}
      className={cn(
        "absolute cursor-move touch-none rounded-sm border outline-none transition-colors",
        selected
          ? "border-2 border-primary bg-primary/10"
          : "border-dashed border-white/70 hover:border-primary/70",
      )}
      style={{
        left: pct(layer.box.x),
        top: pct(layer.box.y),
        width: pct(layer.box.w),
        height: pct(layer.box.h),
        transform: layer.rotation ? `rotate(${layer.rotation}deg)` : undefined,
        boxShadow: "0 0 0 1px rgba(0,0,0,0.35)",
      }}
    >
      {selected && (
        <>
          <span className="absolute -top-5 left-0 rounded bg-primary px-1.5 py-0.5 text-[10px] font-medium text-primary-foreground">
            {LAYER_LABELS[layer.kind]}
          </span>
          <span
            aria-hidden
            data-testid={`resize-${layer.id}`}
            onPointerDown={(e) => drag(e, "resize")}
            className="absolute -bottom-1.5 -right-1.5 h-3 w-3 cursor-nwse-resize rounded-sm border border-white bg-primary"
          />
        </>
      )}
    </div>
  );
}

function PreviewImage({ preview }: { preview: PreviewState }): JSX.Element {
  if (preview.url) {
    return (
      <img
        src={preview.url}
        alt="Prévia da marca d'água"
        className="absolute inset-0 h-full w-full"
        draggable={false}
      />
    );
  }
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-xs text-muted-foreground">
      <ImageOff size={18} />
      {preview.status === "error" ? "Prévia indisponível" : "Gerando a prévia..."}
    </div>
  );
}

export function WatermarkCanvas({
  design,
  preview,
  selectedId,
  onSelect,
  onChange,
}: WatermarkCanvasProps): JSX.Element {
  const surface = useRef<HTMLDivElement>(null);
  const onBox: BoxChange = (id, box) =>
    onChange(updateLayer(design, id, (layer) => ({ ...layer, box })));
  return (
    <div
      ref={surface}
      className="relative aspect-square w-full select-none overflow-hidden rounded-lg border border-border bg-[hsl(var(--surface-raised))]"
      onPointerDown={() => onSelect(null)}
      data-testid="watermark-canvas"
    >
      <PreviewImage preview={preview} />
      {design.layers.map((layer) => (
        <LayerBox
          key={layer.id}
          layer={layer}
          selected={layer.id === selectedId}
          surface={surface}
          onSelect={onSelect}
          onBox={onBox}
        />
      ))}
      {preview.status === "rendering" && (
        <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-black/60 px-2 py-1 text-[10px] text-white">
          <Loader2 size={11} className="animate-spin" /> Renderizando
        </span>
      )}
    </div>
  );
}
